// remark transform that turns AI entity-reference directives into custom
// elements react-markdown can render. Runs AFTER remark-directive, which parses
// `:documentation{id=x version-id=y}` (text directive -> inline chip) and
// `::documentation{id=x}` (leaf directive -> block card) into directive nodes.
// The directive NAME is the entity type (what the BE emits); `id` is the parent
// document, and the optional `version-id` (BE guide §5) pins the chip to the
// exact version the model read (space-separated attribute, not comma).
//
// Container directives (`:::documentation`) are intentionally ignored — only
// inline chips and standalone cards are supported. A directive this transform
// does not own (unknown name, or no id) is rewritten back to its LITERAL source
// text. This matters because remark-directive treats any `:word` in prose as a
// directive — including plain content like `10:30`, `16:9`, `time:approx` — and
// leaving such a node in the tree makes mdast-util-to-hast drop it as an empty
// `<div>`, silently deleting the surrounding text from the answer.
//
// The model occasionally writes an extra attribute just OUTSIDE the braces
// instead of inside them, e.g. `:documentation{id=x} version-id=y.` rather
// than `:documentation{id=x version-id=y}`. remark-directive then only sees
// `id`, and ` version-id=y.` survives as a plain text node right after the
// chip. absorbStrayAttributes() repairs this generically for any attribute
// name a known directive might carry (not just version-id), so it keeps
// working if a future attribute is added without touching this file again.

// Map of directive name -> the hName react-markdown renders via its components.
const KNOWN_DIRECTIVES: Record<string, string> = {
  documentation: 'ai-doc-ref',
};

// The names above, for callers that need to recognise a directive before
// remark-directive can parse it (see maskIncompleteDirective).
export const KNOWN_DIRECTIVE_NAMES = Object.keys(KNOWN_DIRECTIVES);

type NodePosition = { start: { offset?: number }; end: { offset?: number } };

type DirectiveNode = {
  type: string;
  name?: string;
  value?: string;
  attributes?: Record<string, string> | null;
  children?: DirectiveNode[];
  position?: NodePosition;
  data?: { hName?: string; hProperties?: Record<string, unknown> };
};

// Visit every node with its parent and index so a node can be replaced in place.
function walk(
  node: DirectiveNode,
  visit: (
    n: DirectiveNode,
    nodeParent: DirectiveNode | null,
    nodeIndex: number,
  ) => void,
  parent: DirectiveNode | null = null,
  index = -1,
): void {
  visit(node, parent, index);
  node.children?.forEach((child, childIndex) =>
    walk(child, visit, node, childIndex),
  );
}

// Replace an unhandled directive node with a plain-text node carrying its
// original source, so it renders as the literal text the model wrote instead of
// being dropped. Falls back to leaving the node in place if position offsets are
// unavailable (should not happen with remark-parse, which always sets them).
function degradeToText(
  node: DirectiveNode,
  parent: DirectiveNode | null,
  index: number,
  source: string,
): void {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  if (
    !parent?.children ||
    index < 0 ||
    start === undefined ||
    end === undefined
  )
    return;
  // eslint-disable-next-line no-param-reassign
  parent.children[index] = { type: 'text', value: source.slice(start, end) };
}

// Matches one `key=value` or `key="quoted value"` pair at the start of a
// string — quoted so a stray `page="4, 9"` keeps its whole list.
const LEADING_ATTR_PAIR = /^([\w-]+)=(?:"([^"{}]*)"|([^\s{}"]+))/;
// Sentence punctuation glued to a value with no space (e.g. the trailing "."
// in "version-id=<uuid>.") — stripped off the value and put back into the
// surrounding prose rather than being absorbed as part of the attribute.
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

// Absorbs a run of leading `key=value` pairs off the text node immediately
// following a directive into the directive's own attributes, mutating both
// in place. No-ops when the sibling isn't a text node starting with the
// single space that separates a directive from following prose, or contains
// no attribute-shaped prefix.
function absorbStrayAttributes(
  node: DirectiveNode,
  parent: DirectiveNode | null,
  index: number,
): void {
  const sibling = parent?.children?.[index + 1];
  if (!sibling || sibling.type !== 'text' || !sibling.value?.startsWith(' '))
    return;

  let rest = sibling.value.slice(1);
  const absorbed: Record<string, string> = {};
  let lastKey: string | undefined;
  let isLastQuoted = false;
  for (;;) {
    const match = LEADING_ATTR_PAIR.exec(rest);
    if (!match) break;
    const [full, key, quotedValue, bareValue] = match;
    isLastQuoted = quotedValue !== undefined;
    absorbed[key] = quotedValue ?? bareValue;
    lastKey = key;
    const afterValue = rest.slice(full.length);
    // Only swallow the space after a value when another pair follows it —
    // otherwise it's the boundary before ordinary prose and must stay.
    const followedByAnotherPair =
      afterValue.startsWith(' ') && LEADING_ATTR_PAIR.test(afterValue.slice(1));
    rest = followedByAnotherPair ? afterValue.slice(1) : afterValue;
    if (!followedByAnotherPair) break;
  }
  if (!lastKey) return;

  // A quoted value ends at its closing quote; only a bare one can have
  // punctuation glued on.
  const punctuation = isLastQuoted
    ? null
    : TRAILING_PUNCTUATION.exec(absorbed[lastKey]);
  if (punctuation) {
    absorbed[lastKey] = absorbed[lastKey].slice(0, -punctuation[0].length);
    rest = punctuation[0] + rest;
  }

  /* eslint-disable no-param-reassign */
  node.attributes = { ...node.attributes, ...absorbed };
  sibling.value = rest;
  /* eslint-enable no-param-reassign */
}

// Turns the `\|` escapeDirectivePipes adds (so a quote survives a GFM table
// cell) back into a plain `|`. remark-directive keeps the backslash verbatim in
// attribute values, so without this the quote would no longer match its
// citation passage.
function unescapePipes(node: DirectiveNode): void {
  if (!node.attributes) return;
  // eslint-disable-next-line no-param-reassign
  node.attributes = Object.fromEntries(
    Object.entries(node.attributes).map(([key, value]) => [
      key,
      value.split('\\|').join('|'),
    ]),
  );
}

export default function remarkAiDirectives() {
  return (tree: DirectiveNode, file: { toString(): string }): void => {
    const source = String(file);
    walk(tree, (node, parent, index) => {
      if (node.type !== 'textDirective' && node.type !== 'leafDirective')
        return;
      const hName = node.name ? KNOWN_DIRECTIVES[node.name] : undefined;
      if (hName) {
        absorbStrayAttributes(node, parent, index);
        unescapePipes(node);
      }
      const id = node.attributes?.id;
      if (!hName || !id) {
        degradeToText(node, parent, index, source);
        return;
      }
      // Optional version pin; remark-directive parses the hyphenated attribute as
      // the `version-id` key. Surfaced as the camelCase `versionId` hProperty so
      // react-markdown forwards it to the renderer unchanged.
      const versionId = node.attributes?.['version-id'];
      // Optional page citation (`page=4`, `page=4,9`, `page=4-6`), passed on
      // raw: the renderer only shows pages the message's `citations` verify.
      const page = node.attributes?.page;
      // Optional passage on that page (`quote="first … last"`) and video
      // moments (`time=03:00,03:40`), also passed on raw and only acted on
      // when `citations` backs them.
      const quote = node.attributes?.quote;
      const time = node.attributes?.time;
      // Remark transforms mutate the mdast tree in place — this is the idiomatic
      // way to surface a directive as a custom hast element.
      /* eslint-disable no-param-reassign */
      node.data = node.data || {};
      node.data.hName = hName;
      node.data.hProperties = {
        id,
        ...(versionId ? { versionId } : {}),
        ...(page ? { page } : {}),
        ...(quote ? { quote } : {}),
        ...(time ? { time } : {}),
        inline: node.type === 'textDirective',
      };
      /* eslint-enable no-param-reassign */
    });
  };
}
